import React from 'react';
import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';

import config from 'Config/config.json';
/**
 * Wiring Helper
 *
 * This contains functions used to assist with wiring together data and display.
 */

/**
 * Extracts content from array of vehicleSpecList.
 * @param {Array} trims Array of objects that have a type and content key.
 */
export function getMultipageVehicleSpecs(trims) {
  if (!trims || !trims.length) return [];
  return trims.map(trim => trim.items)
    .reduce((result = [], item) => result.concat(item))
    .map(item => ({ url: item.path, title: item.name }));
}

/**
 * Extracts content from array of related content.
 * @param {Array} related Array of objects that have a type and content key.
 * @param {String} key    What key to look for. Defaults to most popular.
 */
export function getRelatedContent(related, key) {
  const item = related.find(({ type }) => type === key);
  if (!item) {
    return [];
  }
  return item.content;
}

/**
 * Extracts content for the ArticleGroup component.
 * @param {Array} related Array of objects that have a type and content key.
 * @param {String} key    What key to look for. Defaults to most popular.
 * @param {Array} injectedItem  Array of objects that have an injected item, and the position where the item should be placed in the array.
 */
export function getArticleGroupContent(
  related,
  key = 'mostPopular',
  injectedItem,
) {
  const relatedContent = getRelatedContent(related, key).map(item => ({
    ...item,
    title: item?.title,
  }));

  // Inject the item into the relatedContent array and remove the last one
  if (injectedItem) {
    // @TODO: pass the position from the template, temp solution
    // relatedContent.splice(injectedItem.position, 1, injectedItem.component);
    relatedContent.splice(-1, 1, injectedItem);
  }

  return relatedContent;
}

/**
 * Extracts content for the ArticleGroup component.
 * @param {Array} related Array of objects that have a type and content key.
 * @param {String} key    What key to look for. Defaults to similar products.
 */
export function getSimilarProductContent(related, key = 'similarProducts') {
  return getArticleGroupContent(related, key).map(item => ({
    ...item,
    // without extra class names ArticleGroup wont render data
    extraClassNames: {
      '-default': true,
    },
  }));
}

/**
 * Extracts content for the ArticleGroup component.
 * @param {Array} related Array of objects that have a type and content key.
 * @param {String} key    What key to look for. Defaults to recommended.
 */
export function getRecommendedContent(related, key = 'recommended') {
  return getArticleGroupContent(related, key);
}

/**
 * Extract a specific value from the data layer, if it exists.
 * @param {String} key   The string to look for.
 * @param {Array} meta   The data layer.
 * @param {*} defaultVal Default value to return if not found.
 */
export function getDataLayerValue(key, meta, defaultVal = null) {
  return (meta.find(({ key: itemKey }) => itemKey === key) || {}).value || defaultVal;
}

/**
 * Gets the title with page number if set.
 * @param {String} title   The page title.
 * @param {Number} pageNum The current page number.
 * @returns {String}       The page title with page number.
 */
export function getPaginatedTitle(title, pageNum) {
  return (!pageNum || pageNum === 1) ? title : `${title} | page ${pageNum}`;
}

/**
 * Wires up the footer promo box.
 * @param {Object} promoSettings Promo box settings.
 * @returns {Object}
 */
export function getFooterPromo(promoSettings = {}) {
  if (!promoSettings) {
    return {};
  }
  const { shortText } = promoSettings;
  if (shortText) {
    return { ...promoSettings, excerpt: shortText };
  }
  return promoSettings;
}

/**
 * Make relative path absolute.
 * @param {String} path    The relative path.
 * @param {String} siteUrl The site base.
 * @returns {String}
 */
export function toAbsolute(path, siteUrl) {
  // Make sure it's not already absolute.
  if (path.indexOf('http') === 0) {
    return path;
  }
  try {
    return new URL(path, siteUrl);
  } catch (e) {
    return path;
  }
}

/**
 * Get index article page contents from props.
 *
 * @param props
 * @return {boolean|*}
 */
export function getIndex(props) {
  const { layoutData: { page: { associatedContent } } } = props;
  const content = associatedContent.find(({ type }) => type === 'indexArticlePageList');
  if (!content) {
    return false;
  }

  return content;
}

/**
 * Reformat header menu parent item.
 *
 * @param element
 * @return {*}
 */
export function reformatHeaderMenuElement(element) {
  if (element.children !== null && Array.isArray(element.children) && element.children.length > 0) {
    /* eslint no-param-reassign: [2, { "props": false }] */
    element.children = element.children.map((item) => {
      const newItem = { ...item };
      if (newItem.item !== null) {
        newItem.url = newItem.item.url;
      }

      return newItem;
    });

    const firstElem = element.children[0];
    if (firstElem && firstElem.item && firstElem.item.__typename === 'TaxonomyIndex' && firstElem.url === element.url) {
      element.viewAll = true;
      delete element.children[0];
    }
  }

  return element;
}

export function addTopLevelFieldToNavigationMenuItemsWithChildren(menuItemsArray) {
  if (!menuItemsArray) return false;
  const arrCopy = cloneDeep(menuItemsArray);
  arrCopy.forEach((parentObject) => {
    reformatHeaderMenuElement(parentObject);
  });

  return arrCopy;
}

export const getRightSideAdConfig = (adCount) => {
  const adConfig = get(config, 'ads[slot-2]');

  if (adConfig) {
    adConfig.extraClassNames = {
      '-sidebar-ad': true,
    };
    adConfig.id = `refresh-right_${adCount}`;
    adConfig.isSidebar = true;
    adConfig.isSkippable = true;
    adConfig.targeting = {
      position: `right_${adCount}`,
      placement: `right_${adCount}`,
      refresh: 'yes',
    };

    return adConfig;
  }

  return false;
};

/**
 * Format article group item for whitepaper
 *
 * @param {*} item
 * @param article
 */
export const formatPageItemForWhitepaper = (item, article) => {
  if (article.__typename && article.__typename === 'WhitepaperResource') {
    item.resourceCategory = {
      resourceLabel: article.contentType ? article.contentType.toLowerCase() : '',
      resourceUrl: article.link || '#',
    };
    item.imageSize = 'resource-card';
  }

  return item;
};
